/**
 * PURPOSE: Dibujar conexiones SVG entre partidos de la llave del evento 17.
 * RESPONSIBILITIES: Medir cards/slots y trazar uniones sin lógica deportiva ni HTTP.
 * COLLABORATORS: Bracket2.jsx, BracketMatchCard.jsx y datos sourceMatchCode.
 * ROUTE: frontend/src/components/FullScreenModal/FullScreenEliminatoria/Bracket/BracketConnectorLayer.jsx
 */
import React, { useCallback, useEffect, useState } from "react";

function buildSelector(value) {
  return String(value || "").replace(/"/g, '\\"');
}

export default function BracketConnectorLayer({ containerRef, rounds }) {
  const [drawing, setDrawing] = useState({ width: 0, height: 0, paths: [] });

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const paths = [];

    (rounds || []).forEach((round) => {
      (round.matches || []).forEach((match) => {
        (match.slots || []).forEach((slot) => {
          if (!slot?.sourceMatchCode) return;

          const source = container.querySelector(
            `[data-match-code="${buildSelector(slot.sourceMatchCode)}"]`
          );
          const targetSlot = container.querySelector(
            `[data-match-code="${buildSelector(
              match.code
            )}"] [data-slot-side="${slot.side}"]`
          );

          if (!source || !targetSlot) return;

          const sourceRect = source.getBoundingClientRect();
          const targetRect = targetSlot.getBoundingClientRect();
          const x1 = sourceRect.right - containerRect.left;
          const y1 =
            sourceRect.top - containerRect.top + sourceRect.height / 2;
          const x2 = targetRect.left - containerRect.left;
          const y2 = targetRect.top - containerRect.top + targetRect.height / 2;
          const middleX = x1 + Math.max(24, (x2 - x1) / 2);

          paths.push({
            key: `${slot.sourceMatchCode}-${match.code}-${slot.side}`,
            d: `M ${x1} ${y1} H ${middleX} V ${y2} H ${x2}`,
            dashed: slot.sourceType === "LOSER",
          });
        });
      });
    });

    setDrawing({
      width: Math.max(container.scrollWidth, container.clientWidth),
      height: Math.max(container.scrollHeight, container.clientHeight),
      paths,
    });
  }, [containerRef, rounds]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const frame = window.requestAnimationFrame(recalculate);
    const timeout = window.setTimeout(recalculate, 120);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(recalculate)
        : null;

    resizeObserver?.observe(container);
    container
      .querySelectorAll("[data-match-code], [data-slot-side]")
      .forEach((element) => resizeObserver?.observe(element));
    window.addEventListener("resize", recalculate);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", recalculate);
    };
  }, [containerRef, recalculate]);

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-0 overflow-visible"
      width={drawing.width}
      height={drawing.height}
      viewBox={`0 0 ${drawing.width} ${drawing.height}`}
      aria-hidden="true"
    >
      {drawing.paths.map((path) => (
        <path
          key={path.key}
          d={path.d}
          fill="none"
          stroke={path.dashed ? "#A78BFA" : "#94A3B8"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={path.dashed ? "5 5" : undefined}
        />
      ))}
    </svg>
  );
}
